import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { queryKeys } from '../lib/queryClient';
import * as schoolService from '../services/schoolService';
import type { 
  CreateSchoolRequest, 
  UpdateSchoolRequest, 
  ListQueryParams 
} from '../types/api';

// School list query
export const useSchools = (params?: ListQueryParams) => {
  return useQuery({
    queryKey: queryKeys.schools.list(params),
    queryFn: () => schoolService.getSchools(params),
    select: (data) => {
      // API returns { schools: School[], pagination: Pagination }
      // Return consistent structure with schools array
      return {
        schools: data.schools || [],
        pagination: data.pagination || null
      };
    },
  });
};

// Single school query
export const useSchool = (id: string | number, options?: { enabled?: boolean; view?: string }) => {
  const { enabled = true, view } = options || {};
  return useQuery({
    queryKey: queryKeys.schools.detail(id),
    queryFn: () => schoolService.getSchool(id.toString(), view),
    select: (data) => data,
    enabled: enabled && !!id,
  });
};

// School areas query
export const useSchoolAreas = (schoolId: number, params?: ListQueryParams) => {
  return useQuery({
    queryKey: queryKeys.schools.areas(schoolId),
    queryFn: () => schoolService.getSchoolAreas(schoolId.toString(), params),
    select: (data) => data || [],
    enabled: !!schoolId,
  });
};

// School classes query
export const useSchoolClasses = (schoolId: number, params?: ListQueryParams) => {
  return useQuery({
    queryKey: queryKeys.schools.classes(schoolId),
    queryFn: () => schoolService.getSchoolClasses(schoolId.toString(), params),
    select: (data) => data || [],
    enabled: !!schoolId,
  });
};

// School students query
export const useSchoolStudents = (schoolId: number, params?: ListQueryParams) => {
  return useQuery({
    queryKey: queryKeys.schools.students(schoolId),
    queryFn: () => schoolService.getSchoolStudents(schoolId.toString(), params),
    select: (data) => data || [],
    enabled: !!schoolId,
  });
};

// Create school mutation
export const useCreateSchool = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateSchoolRequest) => schoolService.createSchool(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.schools.lists() });
      return response;
    },
    onError: (_error: Error) => {
      // Error handling
    },
  });
};

// Update school mutation
export const useUpdateSchool = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: UpdateSchoolRequest }) => 
      schoolService.updateSchool(id.toString(), data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.schools.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.schools.detail(variables.id) });
    },
    onError: (_error: Error) => {
      // Error handling
    },
  });
};

// Delete school mutation
export const useDeleteSchool = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: number) => schoolService.deleteSchool(id.toString()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.schools.lists() });
      notifications.show({
        title: 'Success',
        message: 'School deleted successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete school',
        color: 'red',
      });
    },
  });
};